import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Drawer,
    IconButton,
    Tooltip,
    Typography,
    styled
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IPermissions } from '@pepperhq/auth-client';
import { PERMISSIONS_VIEW_LIST, RoleDisplayConfig, hasViewAccess } from './helpers';
import { addOpacity } from 'lib/helpers';

interface RoleDetailsDrawerProps {
    open: boolean;
    display: RoleDisplayConfig | null;
    permissions: IPermissions | null;
    userCount: number;
    onClose: () => void;
}

const StyledDrawer = styled(Drawer)(({ theme }) => ({
    [`& .MuiDrawer-paper`]: {
        width: 480,
        maxWidth: '100vw',
        padding: theme.spacing(3),
        gap: theme.spacing(1)
    }
}));

const Header = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1)
}));

const GroupHeading = styled(Typography)({
    textTransform: 'uppercase',
    fontWeight: 700,
    opacity: 0.7,
    letterSpacing: 0.9
});

const StyledAccordion = styled(Accordion)(({ theme }) => ({
    border: '1px solid',
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    '&:before': { display: 'none' },
    '&.Mui-expanded': {
        margin: theme.spacing(1, 0),
        borderColor: addOpacity(theme.palette.text.primary, 0.5)
    },
    '& .MuiAccordionSummary-root': {
        minHeight: 'auto',
        padding: theme.spacing(1)
    },
    '& .MuiAccordionSummary-content': {
        margin: theme.spacing(0.5, 0),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1)
    },
    '& .MuiAccordionDetails-root': {
        borderTop: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0, 1, 0, 1)
    }
}));

const GroupDescription = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(1, 0, 1, 1)
}));

const SummaryRight = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary
}));

const ItemRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    padding: theme.spacing(1, 0, 1, 1),
    // borderBottom: `1px solid ${theme.palette.divider}`,
    '&:after': {
        position: 'absolute',
        right: 0,
        bottom: 0,
        width: '95%',
        content: '""',
        opacity: 0.5,
        borderBottom: `1px solid ${theme.palette.divider}`
    },
    '&:last-of-type': {
        borderBottom: 'none',
        '&:after': {
            content: 'none'
        }
    }
}));

const StatusChip = styled(Box, {
    shouldForwardProp: prop => prop !== 'granted'
})<{ granted: boolean }>(({ theme, granted }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: theme.typography.body2.fontSize,
    color: granted ? theme.palette.success.light : theme.palette.error.main
}));

export const RoleDetailsDrawer: React.FC<RoleDetailsDrawerProps> = ({
    open,
    display,
    permissions,
    userCount,
    onClose
}) => {
    const [expanded, setExpanded] = React.useState<string>(() => PERMISSIONS_VIEW_LIST[0]?.name);

    React.useEffect(() => {
        if (display) {
            setExpanded(PERMISSIONS_VIEW_LIST[0]?.name);
        }
    }, [display]);

    const toggleGroup = React.useCallback(
        (name: string) => () => setExpanded(currentName => (currentName === name ? undefined : name)),
        []
    );

    return (
        <StyledDrawer anchor="right" open={open} onClose={onClose}>
            {display && (
                <React.Fragment>
                    <Header>
                        <Box>
                            <Typography variant="h6" fontWeight={600}>
                                {display.displayName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mt={0.5}>
                                {display.description}
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={onClose} aria-label="Close">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Header>
                    <Typography variant="body2" color="text.secondary">
                        {userCount} {userCount === 1 ? 'user' : 'users'} assigned
                    </Typography>
                    {permissions &&
                        PERMISSIONS_VIEW_LIST.map(group => {
                            const isOpen = expanded === group.name;
                            const grantedCount = group.resources.filter(view =>
                                hasViewAccess(permissions, view)
                            ).length;
                            return (
                                <StyledAccordion
                                    key={group.name}
                                    elevation={0}
                                    expanded={isOpen}
                                    onChange={toggleGroup(group.name)}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                                        <GroupHeading variant="subtitle2">{group.name}</GroupHeading>
                                        {!isOpen && (
                                            <SummaryRight>
                                                <Typography variant="body2">
                                                    {grantedCount}/{group.resources.length}
                                                </Typography>
                                            </SummaryRight>
                                        )}
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {group.description && (
                                            <GroupDescription variant="body2" color="text.secondary">
                                                {group.description}
                                            </GroupDescription>
                                        )}
                                        {group.resources.map(view => {
                                            const granted = hasViewAccess(permissions, view);
                                            return (
                                                <ItemRow key={`${group.name}-${view.name}`}>
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        <Typography variant="body1">{view.name}</Typography>
                                                        {view.description && (
                                                            <Tooltip title={view.description} arrow>
                                                                <InfoOutlinedIcon
                                                                    fontSize="small"
                                                                    color="action"
                                                                    sx={{ opacity: 0.6, cursor: 'help' }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                    <StatusChip granted={granted}>
                                                        {granted ? (
                                                            <CheckIcon fontSize="small" />
                                                        ) : (
                                                            <CloseRoundedIcon
                                                                fontSize="small"
                                                                sx={{ opacity: 0.7 }}
                                                            />
                                                        )}
                                                    </StatusChip>
                                                </ItemRow>
                                            );
                                        })}
                                    </AccordionDetails>
                                </StyledAccordion>
                            );
                        })}
                </React.Fragment>
            )}
        </StyledDrawer>
    );
};
